//#region IMPORTS
import { useAppDispatch, useAppSelector } from "../../store/configureStore";
import { fetchInstoreOrderAsync } from "../../features/instoreOrders/instoreOrdersSlice";
import { useLocalInstoreOrderLines } from "../../hooks/useLocalInstoreOrderLines";
import { InstoreOrder } from "../../models/instoreOrder";
import { StoreFormat } from "../../models/storeFormat";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo } from "react";
import InstoreOrderDetailTable from "../instoreOrderDetail/InstoreOrderDetailTable";
import MainLoadingSpinner from "../_layout/MainLoadingSpinner";
//#endregion

//#region INTERFACE
interface Props {
  instoreOrder: InstoreOrder;
}
//#endregion

// The item list of a single instore order, shown in a modal from the orders list
// so you don't have to navigate to the detail page.
//
// Renders the *same* table as the read-only detail view
// (InstoreOrderSummary → InstoreOrderDetailTable), fed by the same redux order
// lines, so the two stay identical by construction rather than by copying markup.
export default function InstoreOrderItemsOverviewModalBody({
  instoreOrder,
}: Props) {
  //#region SETUP
  // Translation
  const { t } = useTranslation();

  // Redux
  const dispatch = useAppDispatch();
  const { instoreOrderLinesLoaded, lastLoadedOrderId } = useAppSelector(
    ({ instoreOrders }) => instoreOrders
  );

  // The list response carries no order lines ("Detail or request only" on
  // `instore_order_lines`), so the detail has to be fetched when this opens.
  // Same thunk as the detail page, which also fills the shared `orderLines`
  // collection the table reads through useLocalInstoreOrderLines.
  useEffect(() => {
    dispatch(fetchInstoreOrderAsync(instoreOrder.id));
  }, [instoreOrder.id, dispatch]);

  // The table needs a StoreFormat for its dynamic amount column; the list row
  // already carries both fields. Mirrors InstoreOrderDetailContainer.
  const storeFormatToUse: StoreFormat = useMemo(
    () => ({
      id: instoreOrder.store_format_id,
      name: instoreOrder.store_format,
      customer_id: 11,
      customer_name: "Rituals",
    }),
    [instoreOrder.store_format_id, instoreOrder.store_format]
  );

  const { localTableData } = useLocalInstoreOrderLines(storeFormatToUse);

  // Tie the loaded flag to *this* order: the shared lines collection may still
  // hold another order's lines from a previously opened modal or detail page.
  const linesReady =
    instoreOrderLinesLoaded && lastLoadedOrderId === instoreOrder.id;
  //#endregion

  //#region RENDER
  return (
    <>
      <header className="border-b border-gray-200 bg-white py-5">
        <div className="-ml-4 -mt-4 flex flex-wrap items-center justify-between sm:flex-nowrap">
          <div className="ml-4 mt-4">
            <h3 className="text-base font-semibold leading-6 text-gray-900">
              {instoreOrder.store_name}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {instoreOrder.order_code}
            </p>
          </div>
          <div className="ml-4 mt-4 flex-shrink-0 text-sm text-gray-500">
            {instoreOrder.total_count} {t("rituals.items")}
          </div>
        </div>
      </header>

      {!linesReady ? (
        <MainLoadingSpinner message="Loading order items..." />
      ) : localTableData.length > 0 ? (
        // No extra scroll wrapper: the table brings its own scroll container, and
        // nesting two produced a second (horizontal) scrollbar.
        <div className="mt-4 border border-gray-200 bg-white">
          <InstoreOrderDetailTable
            // setSelectedCellContext is required but unused here — the perso
            // popover is a modal of its own and the app shows one at a time, hence
            // disablePersoModal. Passing `disabled` instead would grey out the
            // whole table.
            setSelectedCellContext={() => {}}
            storeFormatToUse={storeFormatToUse}
            data={localTableData}
            containerHeight="60vh"
            disablePersoModal
            readOnly
          />
        </div>
      ) : (
        <div className="py-10 text-center text-sm text-gray-500">
          {t("rituals.noItems", "no items")}
        </div>
      )}
    </>
  );
  //#endregion
}
